/**
 * blogHtmlToMarkdown — a blog post's HTML as the light markdown BlogPostPreview reads.
 *
 * 🔴 WHY (CPA Jake, 2026-10-26: "I can't see the post at all when I try to preview it").
 * The blog writer stores the article as `content.html`. The Control Center mapped a
 * `blog-post` approval to a SOCIAL card whose words were `content.text` — absent — so
 * it showed the SEO title and a picture, and he was asked to approve a post he could
 * not read. BlogPostPreview renders light markdown (# headings, - lists, **bold**)
 * WITHOUT innerHTML; this turns the stored HTML into that, so nothing in the
 * model-written HTML can become markup on the page.
 *
 * MIRRORS the text extraction in backend approval/blogApprovalCard.ts (the texted page
 * draws the sanitised HTML itself).
 */

const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  mdash: '—', ndash: '–', rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“', hellip: '…', bull: '•',
}

function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (m, e: string) => {
    if (e[0] === '#') {
      const n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10)
      return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : m
    }
    return ENTITIES[e.toLowerCase()] ?? m
  })
}

export function blogHtmlToMarkdown(html: string): string {
  let s = String(html ?? '')
    // A model sometimes wraps the article in a markdown code fence (10-26: "```html").
    .replace(/^\s*```[a-zA-Z]*\s*\n?/, '').replace(/\n?\s*```\s*$/, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|iframe|noscript|template|svg)\b[\s\S]*?<\/\1\s*>/gi, '')
  s = s
    .replace(/<\s*h1\b[^>]*>/gi, '\n\n# ').replace(/<\s*h2\b[^>]*>/gi, '\n\n## ')
    .replace(/<\s*h[3-6]\b[^>]*>/gi, '\n\n### ')
    .replace(/<\s*li\b[^>]*>/gi, '\n- ')
    .replace(/<\s*(strong|b)\b[^>]*>/gi, '**').replace(/<\s*\/\s*(strong|b)\s*>/gi, '**')
    .replace(/<\s*br\s*\/?>/gi, ' ')
    .replace(/<\s*\/\s*li\s*>/gi, '')
    .replace(/<\s*\/?\s*(p|div|ul|ol|blockquote|section|article|h[1-6])\b[^>]*>/gi, '\n\n')
    .replace(/<[^>]*>/g, '')
  s = decodeEntities(s)
  const lines = s
    .split('\n')
    .map((l) => l.replace(/[ \t]+/g, ' ').trim())
    // An empty <strong></strong> leaves "****".
    .map((l) => l.replace(/\*\*\*\*/g, ''))
  // One list stays one list: drop blank lines between two list items.
  const kept = lines.filter((l, i) => {
    if (l) return true
    const prev = lines.slice(0, i).reverse().find((x) => x)
    const next = lines.slice(i + 1).find((x) => x)
    return !(prev?.startsWith('- ') && next?.startsWith('- '))
  })
  return kept
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
